/**
 * MailboxSwitcher — Design A, locked 2026-07-27; restyled for the inbox revamp
 * (plan §10) 2026-09-17.
 *
 * Replaces the static "Inboxes" page title with an account-style dropdown listing
 * My Inbox plus every unified inbox the caller holds a grant on. It renders on the
 * personal inbox page too, which is the whole point: a departmental inbox has to
 * feel like a place you switch to, not a separate product with its own navigation.
 *
 * Built on a plain button + absolutely-positioned list rather than the shadcn
 * `Select`: this is a navigation control whose rows carry a colour dot, an address
 * summary, an address count and an unread count, and `Select` renders one line of
 * text per option.
 *
 * §10 deltas from the shipped control:
 *   * the role badge is gone — a row now says how many email addresses the inbox
 *     answers on, which is the thing a user switching mailboxes is choosing
 *     between. Roles still govern access elsewhere; they are just not shown as per-row labels.
 *   * unread counts cap at `99+`.
 *   * the footer is gated on org-admin only; ordinary unified-inbox managers
 *     can switch inboxes but do not see the organisation-level management entry.
 *   * 352px popover with a "UNIFIED INBOXES" section header.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Inbox as InboxIcon, Mail, Mailbox, Settings2, Users } from 'lucide-react';

import { MY_INBOX_ID, inboxHue, isMemberOnly } from './inboxRules';
// Extracted so the personal-lane From picker (issue #484) can reuse the locked
// provider styling instead of adding a third provider pill to the tree.
import ProviderBadge from './ProviderBadge';

/** Above this an unread count stops being a number and becomes "a lot". */
const UNREAD_CAP = 99;

/**
 * Resolve the provider icon for a mailbox connection.
 * Gmail → red Mail icon, Outlook → blue Mail icon.
 */
function ProviderIcon({ provider }) {
    if (!provider) return null;
    return (
        <span
            className={`w-4 h-4 mt-0.5 flex-shrink-0 flex items-center justify-center rounded ${
                provider === 'gmail' ? 'text-red-500' : 'text-blue-500'
            }`}
            aria-hidden="true"
        >
            <Mail className="w-3.5 h-3.5" />
        </span>
    );
}

function summaryFor(inbox) {
    if (isMemberOnly(inbox)) {
        // Count both addressing models — a delegated inbox carries
        // `member_smtps` with `member_graph_ids` deliberately empty.
        const count = (inbox.member_graph_ids || []).length + (inbox.member_smtps || []).length;
        return `${count} member mailbox${count === 1 ? '' : 'es'}`;
    }
    return inbox.distro_smtp || 'no distro address';
}

/**
 * Build a summary line for an inbox that includes mailbox provider info.
 *
 * When `mailboxes` (per-inbox connections from `useInboxMailboxes`) are
 * available, the summary shows the first mailbox's email address and a
 * provider count (e.g. "alice@gmail.com · +1 more"). This makes the provider
 * visible at a glance without expanding the row.
 */
function mailboxSummary(inbox, mailboxes = []) {
    const base = summaryFor(inbox);
    if (!mailboxes || mailboxes.length === 0) return base;
    const first = mailboxes[0];
    const email = first?.email || first?.address || '';
    const extra = mailboxes.length > 1 ? ` · +${mailboxes.length - 1} more` : '';
    return email ? `${email}${extra}` : base;
}

/**
 * How many email addresses this inbox actually answers on.
 *
 * Prefers the live mailbox connections when the page has fetched them — those
 * are real, credentialed addresses. Falls back to the inbox record's own
 * addressing model, which is the only thing available for the inboxes the page
 * has not selected: the distro (if any) plus every pooled member mailbox, under
 * both addressing models, exactly as `summaryFor` counts them.
 */
export function addressCount(inbox, mailboxes = []) {
    if (mailboxes && mailboxes.length > 0) return mailboxes.length;
    const members =
        (inbox?.member_graph_ids || []).length + (inbox?.member_smtps || []).length;
    if (isMemberOnly(inbox)) return members;
    const distro = inbox?.distro_smtp || inbox?.distro_graph_user_id ? 1 : 0;
    return distro + members;
}

/** "1 email address" / "3 email addresses" — never "1 email addresses". */
export function addressLabel(count) {
    return `${count} email address${count === 1 ? '' : 'es'}`;
}

/** An unread count as it is rendered: capped, never a five-digit badge. */
export function capUnread(value) {
    const unread = Number(value) || 0;
    if (unread <= 0) return null;
    return unread > UNREAD_CAP ? `${UNREAD_CAP}+` : String(unread);
}

/**
 * May this caller see the inbox-management entry?
 *
 * The switcher can list inboxes for any grant holder, but the management entry
 * navigates to the organisation-level Admin › Unified Inbox surface. Keep that
 * entry exclusive to org admins, including admins who hold no inbox grant.
 */
export function canManageAny(inboxes, user, isAdmin) {
    return Boolean(isAdmin);
}

export default function MailboxSwitcher({
    inboxes = [],
    selectedId = MY_INBOX_ID,
    onSelect,
    user,
    unreadByInbox = {},
    isAdmin = false,
    // Navigates to the organisation-level admin unified-inbox page. The
    // component stays router-free so it can be rendered in a bare test.
    onManageInboxes,
    label = 'Inboxes',
    // Per-inbox mailbox connections from useInboxMailboxes, keyed by inbox_id.
    // Each value is an array of { provider, email, connection_id, status }.
    mailboxesByInbox = {},
}) {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const containerRef = useRef(null);

    const options = useMemo(
        () => [
            { id: MY_INBOX_ID, name: 'My Inbox', personal: true },
            ...inboxes,
        ],
        [inboxes]
    );

    const selected = options.find(o => (o.inbox_id || o.id) === selectedId) || options[0];

    const canManage = useMemo(
        () => canManageAny(inboxes, user, isAdmin),
        [inboxes, user, isAdmin]
    );

    // Re-seed the highlight from the current selection each time the list opens,
    // so keyboard navigation starts where the eye already is.
    useEffect(() => {
        if (!open) return;
        const index = options.findIndex(o => (o.inbox_id || o.id) === selectedId);
        setHighlight(index >= 0 ? index : 0);
    }, [open, options, selectedId]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocumentClick = event => {
            if (!containerRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocumentClick);
        return () => document.removeEventListener('mousedown', onDocumentClick);
    }, [open]);

    const choose = useCallback(
        option => {
            setOpen(false);
            onSelect?.(option.personal ? MY_INBOX_ID : option.inbox_id);
        },
        [onSelect]
    );

    const onKeyDown = useCallback(
        event => {
            if (!open) {
                if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOpen(true);
                }
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlight(i => Math.min(i + 1, options.length - 1));
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlight(i => Math.max(i - 1, 0));
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                const option = options[highlight];
                if (option) choose(option);
            }
        },
        [choose, highlight, open, options]
    );

    // One renderer, called twice, so the "UNIFIED INBOXES" header can sit between
    // My Inbox and the granted inboxes WITHOUT the index drifting away from
    // `options` — the keyboard highlight indexes into that array.
    const renderOption = (option, index) => {
        const id = option.inbox_id || option.id;
        const isSelected = id === selectedId;
        const unread = capUnread(unreadByInbox[id]);
        const inboxMailboxes = option.personal ? [] : (mailboxesByInbox[id] || []);
        return (
            <button
                key={id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(option)}
                data-testid={`mailbox-option-${id}`}
                className={`w-full flex items-start gap-2.5 px-3 py-2 text-left transition-colors ${
                    index === highlight ? 'bg-stone-50' : ''
                }`}
            >
                {option.personal ? (
                    <InboxIcon className="w-4 h-4 mt-0.5 text-stone-500 flex-shrink-0" />
                ) : inboxMailboxes.length > 0 ? (
                    <ProviderIcon provider={inboxMailboxes[0]?.provider} />
                ) : (
                    <span
                        aria-hidden="true"
                        className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ background: inboxHue(id) }}
                    />
                )}
                <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-stone-900 truncate">
                            {option.personal ? label : option.name}
                        </span>
                        {!option.personal && (
                            <span
                                data-testid={`mailbox-addresses-${id}`}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 flex-shrink-0 whitespace-nowrap"
                            >
                                {addressLabel(addressCount(option, inboxMailboxes))}
                            </span>
                        )}
                        {inboxMailboxes.map(m => (
                            <ProviderBadge key={m.connection_id || m.email} provider={m.provider} />
                        ))}
                    </span>
                    <span className="block text-xs text-stone-400 truncate">
                        {option.personal
                            ? 'Your own mail'
                            : mailboxSummary(option, inboxMailboxes)}
                    </span>
                </span>
                {unread && (
                    <span className="text-xs font-medium text-stone-500 mt-0.5 tabular-nums">
                        {unread}
                    </span>
                )}
                {isSelected && (
                    <Check className="w-3.5 h-3.5 text-stone-500 mt-1 flex-shrink-0" />
                )}
            </button>
        );
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                onKeyDown={onKeyDown}
                aria-haspopup="listbox"
                aria-expanded={open}
                data-testid="mailbox-switcher-trigger"
                className="flex items-center gap-1.5 rounded-lg px-1.5 py-0.5 -ml-1.5 hover:bg-stone-100 transition-colors"
            >
                <h1
                    className="text-xl font-bold text-stone-900"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                    {selected?.personal ? label : selected?.name}
                </h1>
                <ChevronDown className="w-4 h-4 text-stone-400" />
            </button>

            {open && (
                <div
                    role="listbox"
                    aria-label="Switch mailbox"
                    data-testid="mailbox-switcher-list"
                    className="absolute left-0 top-full mt-1 z-50 w-[352px] rounded-xl border border-stone-200 bg-white shadow-lg py-1"
                >
                    {renderOption(options[0], 0)}

                    {inboxes.length > 0 && (
                        <div
                            data-testid="mailbox-switcher-section-unified"
                            className="px-3 pt-2 pb-1 text-[10.5px] font-semibold tracking-wide"
                            style={{ color: '#A29B95' }}
                        >
                            UNIFIED INBOXES
                        </div>
                    )}

                    {options.slice(1).map((option, index) => renderOption(option, index + 1))}

                    {inboxes.length === 0 && (
                        <p className="px-3 py-2 text-xs text-stone-400 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />
                            No departmental inboxes yet.
                        </p>
                    )}

                    {/* Only org admins can reach Admin › Unified Inbox from this
                        organisation-level management entry. */}
                    {canManage && (
                        <>
                            <div className="my-1 border-t border-stone-100" />
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onManageInboxes?.(selectedId);
                                }}
                                data-testid="mailbox-switcher-manage"
                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-stone-500 hover:bg-stone-50"
                            >
                                <Settings2 className="w-3.5 h-3.5" />
                                Manage Inboxes
                                {isAdmin && (
                                    <span className="text-stone-300">→ Admin › Unified Inbox</span>
                                )}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export { Mailbox as MailboxSwitcherIcon };
